import React, { forwardRef } from 'react'

import Box from '../box'
import Pressable from '../pressable'
import Text, { TextBlock, type TextProps } from '../text'
import {
  border,
  coloring,
  composeGroups,
  createView,
  flexContainer,
  flexItem,
  layout,
  margins,
  paddings,
  positioning,
  sizing,
  spacing,
  type KitViewProps
} from '../_support'

/* -----------------------------------------------------------------------------
 * Button
 *
 * The @walrus/common Button, part for part: a Pressable around an outer View
 * (borders, margins, size) and an inner View (padding, color, alignment) that
 * is a linear gradient for the *Gradient variants. Label props go to a
 * TextBlock, so string children and nested Text pick up the variant's font and
 * color.
 * -------------------------------------------------------------------------- */

const outerStyles = composeGroups(border, flexItem, layout, margins, sizing)
const innerStyles = composeGroups(flexContainer, flexItem, paddings, coloring)

const ButtonOuter = createView({
  displayName: 'ButtonOuter',
  groups: outerStyles
})
const ButtonInner = createView({
  displayName: 'ButtonInner',
  groups: innerStyles
})
const GradientInner = createView({
  displayName: 'ButtonGradient',
  groups: composeGroups(
    border,
    coloring,
    flexContainer,
    flexItem,
    layout,
    paddings,
    positioning,
    sizing,
    spacing
  )
})

// react-native-web-linear-gradient with the kit's variants: left to right, 90deg
const gradients: Record<string, string> = {
  violetGradient: 'linear-gradient(1.5707963267948966rad,#5162F4,#A872FF)',
  yellowGradient: 'linear-gradient(1.5707963267948966rad,#FFE897,#FFD794)',
  orangeGradient: 'linear-gradient(1.5707963267948966rad,#FFC990,#FEAC88)',
  redGradient: 'linear-gradient(1.5707963267948966rad,#FD9E84,#F97A8A)'
}

const outerKeys = new Set(Object.keys(outerStyles))
const buttonKeys = new Set([
  ...Object.keys(outerStyles),
  ...Object.keys(innerStyles)
])

type Variant =
  | 'gray'
  | 'activeGray'
  | 'green'
  | 'paleGreen'
  | 'paleRed'
  | 'pink'
  | 'violet'
  | 'paleViolet'
  | 'punchThroughBlue'
  | 'punchThroughPurple'
  | 'punchThroughGreen'
  | 'punchThroughViolet'
  | 'punchThroughGray'
  | 'violetGradient'
  | 'yellowGradient'
  | 'orangeGradient'
  | 'redGradient'
  | 'secondary'
  | 'tertiary'

const variants: Record<Variant, { button: KitViewProps; text: TextProps }> = {
  gray: { button: { bg: 'shade03' }, text: { textColor: 'shade06' } },
  activeGray: { button: { bg: 'shade03' }, text: { textColor: 'purple' } },
  paleGreen: { button: { bg: 'paleGreen' }, text: { textColor: 'white' } },
  green: { button: { bg: 'green' }, text: { textColor: 'white' } },
  pink: { button: { bg: 'pink' }, text: { textColor: 'white' } },
  violet: { button: { bg: 'violet' }, text: { textColor: 'white' } },
  paleViolet: { button: { bg: 'paleViolet' }, text: { textColor: 'violet' } },
  paleRed: { button: { bg: 'paleRed' }, text: { textColor: 'white' } },
  punchThroughBlue: {
    button: { bg: 'white' },
    text: { textColor: 'darkBlue' }
  },
  punchThroughPurple: {
    button: { bg: 'white' },
    text: { textColor: 'purple' }
  },
  punchThroughGreen: { button: { bg: 'white' }, text: { textColor: 'green' } },
  punchThroughViolet: {
    button: { bg: 'white' },
    text: { textColor: 'violet' }
  },
  punchThroughGray: { button: { bg: 'white' }, text: { textColor: 'shade06' } },
  violetGradient: { button: { bg: 'blue' }, text: { textColor: 'white' } },
  yellowGradient: { button: { bg: 'yellow' }, text: { textColor: 'purple' } },
  orangeGradient: { button: { bg: 'orange' }, text: { textColor: 'purple' } },
  redGradient: { button: { bg: 'red' }, text: { textColor: 'purple' } },
  secondary: { button: { bg: 'walrusBlue500' }, text: { textColor: 'white' } },
  tertiary: {
    button: { bg: 'walrusBlue050' },
    text: { textColor: 'walrusBlue500' }
  }
}

const sizes = {
  fit: {
    button: { py: '08', px: '16', borderRadius: 'pill', width: 'fit-content' },
    text: { font: 'text.12.bold' }
  },
  small: { button: { p: '08' }, text: { font: 'text.14.semibold' } },
  medium: { button: { px: '24', py: '16' }, text: { font: 'text.14.bold' } },
  mediumSmall: {
    button: { px: '16', py: '12' },
    text: { font: 'text.12.bold' }
  }
} as const

const shapes = {
  rounded: { borderRadius: 'medium' },
  roundedLarge: { borderRadius: 'large' },
  pill: { borderRadius: 'pill' },
  rectangular: { borderRadius: 'small' }
} as const

export type ButtonProps = Omit<TextProps, 'variant'> & {
  deactivated?: boolean
  disabled?: boolean
  onPress?: (event: React.SyntheticEvent) => void
  overlay?: React.ReactNode
  variant?: Variant
  size?: keyof typeof sizes
  shape?: keyof typeof shapes
}

// splits the button's props between the outer and the inner part
export type BaseButtonProps = KitViewProps & { variant?: Variant }

export const BaseButton = ({ variant, ...props }: BaseButtonProps) => {
  const outer: KitViewProps = {}
  const inner: KitViewProps = {}
  Object.keys(props).forEach(prop => {
    ;(outerKeys.has(prop) ? outer : inner)[prop] = props[prop]
  })
  const gradient = gradients[variant]
  return (
    <ButtonOuter
      style={{ flexDirection: 'row', overflow: 'hidden' }}
      {...outer}
    >
      {gradient ? (
        <GradientInner
          flex='1 1 auto'
          flexDirection='row'
          justifyContent='center'
          {...inner}
          style={[inner.style, { backgroundImage: gradient }]}
        />
      ) : (
        <ButtonInner
          flex='1 1 auto'
          flexDirection='row'
          justifyContent='center'
          {...inner}
        />
      )}
    </ButtonOuter>
  )
}

const childrenAreStrings = (children: React.ReactNode) =>
  !!children &&
  (typeof children === 'string' ||
    (Array.isArray(children) &&
      children.every(child => typeof child === 'string')))

const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      children,
      deactivated = false,
      disabled = false,
      onPress,
      overlay,
      shape = 'pill',
      size = 'medium',
      variant = 'violetGradient',
      ...props
    },
    ref
  ) => {
    const buttonProps: KitViewProps = {}
    const textProps: TextProps = {}
    Object.keys(props).forEach(prop => {
      ;(buttonKeys.has(prop) ? buttonProps : textProps)[prop] = props[prop]
    })
    const active = !disabled && !deactivated

    return (
      <Pressable
        ref={ref}
        {...(active
          ? { onPress, ...(buttonProps.flex ? { flex: buttonProps.flex } : {}) }
          : {})}
      >
        <BaseButton
          accessibilityRole='button'
          variant={variant}
          alignItems='center'
          style={{ cursor: disabled ? 'default' : 'pointer' }}
          {...shapes[shape]}
          {...sizes[size].button}
          {...variants[variant].button}
          {...buttonProps}
        >
          <TextBlock
            textAlign='center'
            opacity={overlay ? 0 : 1}
            {...sizes[size].text}
            {...variants[variant].text}
            {...textProps}
          >
            {childrenAreStrings(children) ? <Text>{children}</Text> : children}
          </TextBlock>
          {overlay && (
            <Box
              alignItems='center'
              justifyContent='center'
              position='absolute'
              top={0}
              left={0}
              right={0}
              bottom={0}
            >
              {overlay}
            </Box>
          )}
        </BaseButton>
      </Pressable>
    )
  }
)
Button.displayName = 'Button'

export default Button
